/**
 * 何段かの見出し。列ごとに、上の段から下の段への見出しの並び（path）を持ち、続く列の同じ見出しを1つのセルにまとめる。
 * 一番下の見出しは、そこから最下段までの高さを使い、名前と単位を2行で示す。押すと並べ替える。
 * 見出しの行はスクロールしても動かないよう、段ごとの高さを決めて、段ごとに上からの位置をずらす。
 * 縦線は、複数の列をまとめた見出しの左右の境目に、その見出しの段から最下段まで引く（lineLevels）。
 */

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  TableCell,
  TableRow,
  TableSortLabel,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { SortState } from './sorting';

/** 見出しの1行の段の高さと、名前と単位の2行がある最下段の高さ */
const ROW_HEIGHT = 24;
const TWO_LINE_ROW_HEIGHT = 48;

/**
 * 見出しのセル。段の高さに収まるよう余白を詰め、消費項目の名前（Athena など）を大文字にしない。
 * テーマ（MuiTableHead）と表の余白（compactTableSx）の指定より優先させるため、クラスを重ねて詳細度を上げる
 */
const headCellSx = { '&&&': { py: 0.25, lineHeight: 1.4, textTransform: 'none' } } as const;
const lineSx = { borderLeft: 1, borderLeftColor: 'divider' } as const;

export interface HeaderColumn {
  key: string;
  /** 上の段から下の段への見出し。最後が列の名前 */
  path: string[];
  /** path の段ごとの、見出しに添える説明。まとめたセルでは、その先頭の列のものを使う */
  pathHints?: (string | undefined)[];
  unit?: string;
  numeric?: boolean;
  /** 並べ替えられる列 */
  sortable?: boolean;
  /** 見出しのセルの中身を差し替える（お気に入りの星など） */
  content?: ReactNode;
  /** 見出しの名前の前に置く部品（下の行をまとめて開け閉めする印など） */
  leading?: ReactNode;
  /** 横スクロールしても動かない列のセルの設定 */
  cell?: { className?: string; sx: SxProps<Theme> };
  ariaLabel?: string;
}

interface Props {
  columns: HeaderColumn[];
  sort: SortState | null;
  onSort: (key: string, numeric: boolean) => void;
}

/** 段ごとの高さ。最下段は、そこに名前と単位の2行の見出しがあれば2行分 */
function heights(columns: HeaderColumn[], depth: number): number[] {
  const twoLines = columns.some((c) => c.path.length === depth && c.unit);
  return Array.from({ length: depth }, (_, i) =>
    i === depth - 1 && twoLines ? TWO_LINE_ROW_HEIGHT : ROW_HEIGHT,
  );
}

/**
 * 列ごとの、左の境目に縦線を引き始める段。引かない列は null。
 * 左の列と見出しが分かれる段で、どちらかの列がその段で複数の列をまとめた見出し（最下段でない見出し）なら、その段から引く
 */
export function lineLevels(columns: { path: string[] }[]): (number | null)[] {
  return columns.map((col, i) => {
    if (i === 0) return null;
    const a = columns[i - 1].path;
    const b = col.path;
    let level = 0;
    while (level < a.length && level < b.length && a[level] === b[level]) level++;
    const grouped = (p: string[]) => p.length > level + 1;
    return grouped(a) || grouped(b) ? level : null;
  });
}

export function GroupedHeader({ columns, sort, onSort }: Props) {
  const depth = Math.max(...columns.map((c) => c.path.length));
  const rowHeights = heights(columns, depth);
  const top = (level: number) => rowHeights.slice(0, level).reduce((a, b) => a + b, 0);
  const lines = lineLevels(columns);
  const lineAt = (i: number, level: number) => {
    const from = lines[i];
    return from !== null && from <= level;
  };
  const rows: ReactNode[][] = Array.from({ length: depth }, () => []);

  for (let level = 0; level < depth; level++) {
    let i = 0;
    while (i < columns.length) {
      const col = columns[i];
      if (level >= col.path.length) {
        i++;
        continue;
      }
      if (level === col.path.length - 1) {
        rows[level].push(
          <LeafCell
            key={col.key}
            column={col}
            rowSpan={depth - level}
            top={top(level)}
            line={lineAt(i, level)}
            sort={sort}
            onSort={onSort}
          />,
        );
        i++;
        continue;
      }
      const prefix = col.path.slice(0, level + 1).join('\u0000');
      let span = 1;
      while (
        i + span < columns.length &&
        columns[i + span].path.length > level + 1 &&
        columns[i + span].path.slice(0, level + 1).join('\u0000') === prefix
      ) {
        span++;
      }
      const hint = col.pathHints?.[level];
      rows[level].push(
        <TableCell
          key={`${level}:${col.key}`}
          colSpan={span}
          align="center"
          sx={{ ...headCellSx, top: top(level), ...(lineAt(i, level) ? lineSx : {}) }}
        >
          {col.path[level]}
          {hint && <HeaderHint text={hint} />}
        </TableCell>,
      );
      i += span;
    }
  }

  return (
    <>
      {rows.map((cells, level) => (
        <TableRow key={level} sx={{ height: rowHeights[level] }}>
          {cells}
        </TableRow>
      ))}
    </>
  );
}

/** 見出しに添える説明。「i」のアイコンにマウスを乗せるか、キーボードで選ぶと吹き出しで出す */
function HeaderHint({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow>
      <InfoOutlinedIcon
        tabIndex={0}
        aria-label={text}
        color="action"
        sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'text-bottom', cursor: 'pointer' }}
      />
    </Tooltip>
  );
}

function LeafCell({
  column,
  rowSpan,
  top,
  line,
  sort,
  onSort,
}: {
  column: HeaderColumn;
  rowSpan: number;
  top: number;
  /** 左の境目に縦線を引く */
  line: boolean;
  sort: SortState | null;
  onSort: (key: string, numeric: boolean) => void;
}) {
  const name = column.path[column.path.length - 1];
  const label = (
    <>
      <Box component="span" display="block">
        {name}
      </Box>
      {column.unit && (
        <Box component="span" display="block" fontWeight="normal">
          {column.unit}
        </Box>
      )}
    </>
  );
  const active = sort?.key === column.key;
  const cellSx = (column.cell?.sx ?? {}) as object;
  const body =
    column.content ??
    (column.sortable ? (
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : column.numeric ? 'desc' : 'asc'}
        onClick={() => onSort(column.key, !!column.numeric)}
        sx={{ flexDirection: column.numeric ? 'row-reverse' : 'row', textAlign: 'inherit' }}
      >
        <Box component="span">{label}</Box>
      </TableSortLabel>
    ) : (
      label
    ));
  return (
    <TableCell
      rowSpan={rowSpan}
      className={column.cell?.className}
      aria-label={column.ariaLabel}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      sx={{
        ...headCellSx,
        ...cellSx,
        ...(line ? lineSx : {}),
        top,
        textAlign: column.numeric ? 'right' : 'left',
      }}
    >
      {column.leading ? (
        <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
          {column.leading}
          {body}
        </Box>
      ) : (
        body
      )}
    </TableCell>
  );
}
